import { FC, useEffect, useMemo } from "react";
import { Controller } from "react-hook-form";
import { useSelector } from "react-redux";

import { SideForm } from "./SideForm";
import { ArchwayDetails } from "./ArchwayDetails";
import { InvoiceTable } from "./InvoiceTable";
import { InvoiceHeader } from "./InvoiceHeader";
import { JobsList } from "./JobsList";
import {
  Checkbox,
  CurrencySelect,
  Datepicker,
  Input,
  Label,
  TextArea,
} from "../../common";
import { StatusPill } from "../../pills";
import { CURRENCY_MAP, DEFAULT_FIAT_CURRENCY } from "../../../constants";
import { useUpdateInvoiceForm } from "../../../hooks/useUpdateInvoiceForm";
import { selectCurrentUser } from "../../../store/reducers/user";
import { StatusSet } from "../../../types/GlobalTypes";
import {
  Invoice,
  InvoiceStatus,
  CurrencyType,
  Currency,
} from "../../../types/InvoiceTypes";
import { formatDate } from "../../../utils/date";
import { toastError } from "../../../utils/toasts";
import { InvoiceSummary } from "./InvoiceSummary";

type Props = {
  data: Invoice;
};

export const InvoiceForm: FC<Props> = ({ data }) => {
  const {
    number,
    status,
    user_is_creator,
    user_is_issuer,
    user_is_payer,
    approved_by_creator,
    note,
    issued_at,
    due_at,
    declined_reason,
    payer_id,
    currency,
  } = data;

  const {
    allowed_incoming_currencies,
    allowed_outgoing_currencies,
    outgoing_currencies_for_incoming_currency,
  } = useSelector(selectCurrentUser);

  const selectedIncomingCurrency = currency || DEFAULT_FIAT_CURRENCY;

  const currencyDetails = CURRENCY_MAP[selectedIncomingCurrency];

  const allowedOutgoingCurrencies = outgoing_currencies_for_incoming_currency
    ? outgoing_currencies_for_incoming_currency[selectedIncomingCurrency]
    : [];

  const isDraft = status === InvoiceStatus.DRAFT;
  const isDeclined = !!declined_reason;

  const enableInvoiceNumber =
    isDraft && ((user_is_issuer && !isDeclined) || user_is_creator);

  const needsCreatorReview =
    isDraft && user_is_creator && (!approved_by_creator || isDeclined);

  const {
    control,
    error,
    isError,
    isLoading,
    isSuccess,
    fields,
    append,
    remove,
  } = useUpdateInvoiceForm(data);

  useEffect(() => {
    if (isError) {
      const errList = Object.values((error as any).data).flat(1);

      errList.forEach((item: any) => {
        if (typeof item === "object" && Object.keys(item).length === 0) {
          return;
        }

        if (item.rate) {
          item.rate.forEach((msg: string) => toastError(`Job sum: ${msg}`));
        } else if (item.description) {
          item.description.forEach((msg: string) =>
            toastError(`Job description: ${msg}`)
          );
        } else {
          toastError(`${item}`);
        }
      });
    }
  }, [isLoading, isSuccess, isError, error]);

  const showBankDetails =
    data?.status !== InvoiceStatus.DRAFT &&
    data?.status !== InvoiceStatus.CANCELLED;

  const statusPill = (
    <StatusPill
      set={StatusSet.INVOICE}
      status={status}
      handler={(status: string | number, statusConfig) =>
        status === InvoiceStatus.DRAFT && approved_by_creator
          ? { text: "Pending approval", color: "text-brown-500", icon: "Timer" }
          : statusConfig
      }
    />
  );

  const paymentDetailsMessage = useMemo(() => {
    if (currencyDetails.type === CurrencyType.FIAT) {
      const sepaText =
        currencyDetails.value === Currency.EUR ? (
          <>
            {" "}
            through{" "}
            <span className="font-extrabold">SEPA/SEPA-Instant only</span>
          </>
        ) : undefined;

      return (
        <>
          Use these details to send{" "}
          <span className="font-extrabold uppercase">
            {currencyDetails.value}
          </span>
          {sepaText}. <span className="font-extrabold">IMPORTANT</span>: You
          must include the{" "}
          <span className="font-extrabold">REF # / MEMO / Comment</span> when
          creating the payment, or it may not be processed.
        </>
      );
    }

    return (
      <>
        Use these details to send{" "}
        <span className="font-extrabold uppercase">
          {currencyDetails.value}
        </span>
        . Make sure you pay to the right{" "}
        <span className="font-extrabold">ADDRESS</span>
      </>
    );
  }, [currencyDetails]);

  return (
    <form className="flex flex-col gap-12 pb-8">
      <div className="flex flex-row gap-x-6 items-center">
        {enableInvoiceNumber ? (
          <>
            <div className="font-source-serif text-5xl">Invoice</div>

            <Controller
              name="number"
              control={control}
              render={({ field }) => (
                <Input type="string" maxLength="10" {...field} prefix="#" />
              )}
            />

            <div className="ml-auto">{statusPill}</div>
          </>
        ) : (
          <>
            <InvoiceHeader id={number} />

            <div className="ml-auto">{statusPill}</div>
          </>
        )}
      </div>

      <div className="grid grid-cols-2 gap-x-6">
        <SideForm
          side="from"
          data={data}
          editable={isDraft && (user_is_issuer || false)}
        />

        <SideForm
          side="to"
          data={data}
          editable={isDraft && (user_is_payer || !payer_id || false)}
        />
      </div>

      {needsCreatorReview ? (
        <div className="flex flex-row gap-x-6 justify-stretch">
          <Label
            className="w-full"
            label="Issue date"
            labelClassName="mb-4 w-full uppercase"
          >
            <Controller
              name="issued_at"
              control={control}
              render={({ field }) => (
                <Datepicker
                  note="The date when the invoice is created"
                  {...field}
                />
              )}
            />
          </Label>

          <Label
            className="w-full"
            label="Due date to pay"
            labelClassName="mb-4 uppercase"
          >
            <Controller
              name="due_at"
              control={control}
              render={({ field }) => (
                <Datepicker
                  note="Payment deadline for the customer"
                  {...field}
                />
              )}
            />
          </Label>
        </div>
      ) : (
        <div className="flex flex-row gap-x-6">
          <Label
            className="w-full"
            label="Issue date"
            labelClassName="mb-1 theme-caption uppercase"
          >
            <span className="theme-text">
              {issued_at && formatDate(issued_at)}
            </span>
          </Label>

          <Label
            className="w-full"
            label="Due date"
            labelClassName="mb-1 theme-caption uppercase"
          >
            <span className="theme-text">{due_at && formatDate(due_at)}</span>
          </Label>
        </div>
      )}

      <section>
        {needsCreatorReview ? (
          <div className="flex flex-col gap-4 border-t border-gray-50 pt-6">
            <Label
              label="Invoice currency"
              className="justify-between"
              labelClassName="mb-4 uppercase"
              inline
            >
              <Controller
                name="currency"
                control={control}
                render={({ field }) => (
                  <CurrencySelect
                    {...field}
                    allowedCurrencies={allowed_incoming_currencies}
                  />
                )}
              />
            </Label>

            <Label
              label="Currency you'll get"
              className="justify-between"
              labelClassName="mb-4 uppercase !w-fit"
              inline
            >
              <Controller
                name="outgoing_currency"
                control={control}
                render={({ field }) => (
                  <CurrencySelect
                    {...field}
                    allowedCurrencies={allowed_outgoing_currencies.filter((c) =>
                      allowedOutgoingCurrencies.includes(c)
                    )}
                    otherComingSoon
                  />
                )}
              />
            </Label>

            <InvoiceTable
              control={control}
              fields={fields}
              append={append}
              remove={remove}
            />
          </div>
        ) : (
          <JobsList invoice={data} />
        )}

        <InvoiceSummary className="mt-8" />

        {needsCreatorReview && (
          <Controller
            name="show_transaction_data_on_public_view"
            control={control}
            render={({ field, field: { value } }) => (
              <Checkbox {...field} checked={value}>
                <span className="text-gray-300">
                  Show transaction data on the public view
                </span>
              </Checkbox>
            )}
          />
        )}
      </section>

      <ArchwayDetails
        show={showBankDetails}
        message={paymentDetailsMessage}
        currency={currency}
        data={data.incoming_payment_details}
      />

      {needsCreatorReview ? (
        <Label label="Notes for client" labelClassName="mb-4 uppercase">
          <Controller
            name="note"
            control={control}
            render={({ field }) => (
              <TextArea
                className="min-h-[124px]"
                placeholder="Optional"
                {...field}
              />
            )}
          />
        </Label>
      ) : (
        note && (
          <Label
            label="Note"
            labelClassName="mb-1 w-72 theme-caption uppercase"
          >
            <span className="theme-text break-all">{note}</span>
          </Label>
        )
      )}
    </form>
  );
};
